import InfoIcon from "@mui/icons-material/Info";
import { Box, IconButton, Menu, MenuItem } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import { useCheckUserPermission } from "../../../../api/hooks/useCheckUserPermission";
import useColors from "../../../../api/hooks/useColors";
import { remediationKeys } from "../../../../api/queryKeys";
import CustomToolTip from "../../../../components/CustomTooltip";
import CustomToolTipWithPermission from "../../../../components/CustomToolTipWithPermission";
import CircleOption from "../../../../components/filterOption/CircleOption";
import { Toast } from "../../../../components/Toast";
import {
  PLATFORM_USER_ACTIONS,
  RemediationPriority,
} from "../../../../utils/constants";
import { getPrettyDateTime, useLocalTime } from "../../../../utils/timestamp";

const CustomPrioritySelect = ({ row, renderValue }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const { updateRemediationCustomPriority, trackEvent } = useAPI();
  const { permitted: hasPermission } = useCheckUserPermission(
    PLATFORM_USER_ACTIONS.UPDATE_REMEDIATION_CUSTOM_PRIORITY
  );
  const queryClient = useQueryClient();
  const showLocalTime = useLocalTime();
  const open = Boolean(anchorEl);
  const colors = useColors();
  const handleClick = (event) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };
  const options = Object.entries(RemediationPriority)
    .filter(([key, value]) => key !== "0")
    .map(([key, value]) => ({
      value: key,
      label: value,
    }))
    .reverse();

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (optionValue) => {
    trackEvent(
      `user updated remediation custom priority ${row.remediation_id} to ${optionValue}`
    );
    updateRemediationCustomPriority({
      remediation_id: row.remediation_id,
      custom_priority: optionValue,
    }).then(() => {
      queryClient.invalidateQueries({
        queryKey: remediationKeys.all,
      });
      handleClose();
      Toast.success("Priority updated successfully");
    });
  };

  const currentPriority =
    row.custom_priority === 0 ? row.priority : row.custom_priority;
  const isCustomPriority = row.custom_priority !== 0;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <CustomToolTipWithPermission
        title={""}
        permission={hasPermission}
        placement="top"
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CustomToolTip
            title={!hasPermission ? "" : "Adjust priority level"}
            placement="top"
          >
            <Box
              onClick={hasPermission ? handleClick : null}
              sx={{ display: "inline-block", cursor: "pointer" }}
            >
              {renderValue ? renderValue(currentPriority) : currentPriority}
            </Box>
          </CustomToolTip>
        </Box>
      </CustomToolTipWithPermission>

      {isCustomPriority && row.priority !== currentPriority && (
        <CustomToolTip
          title={`This priority was manually updated ${
            row.priority_details
              ? `by ${row.priority_details?.changed_by} on ${getPrettyDateTime(
                  row.priority_details?.changed_at?.timestamp_utc,
                  showLocalTime
                )}`
              : ""
          }. Breeze originally prioritized it as '${
            RemediationPriority[row.priority]
          }'`}
          placement="right"
          sx={{ maxWidth: "260px" }}
        >
          <IconButton
            size="small"
            sx={{ padding: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <InfoIcon
              fontSize="small"
              sx={{
                width: "20px",
                height: "20px",
                color: colors.grey_48percent,
              }}
            />
          </IconButton>
        </CustomToolTip>
      )}

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "priority-select-button",
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        sx={{
          color: colors.contrast_text_dark,
          "& .MuiPaper-root": {
            borderRadius: "8px",
            minWidth: "110px",
            border: "1px solid " + colors.divider2,
            boxShadow: "0px 1px 4px 0px " + colors.black_10percent,
          },
          "& .MuiList-root": {
            padding: "8px",
            display: "flex",
            flexDirection: "column",
            gap: "4px",
          },
        }}
      >
        {options
          .filter((option) => option.value !== currentPriority)
          .map((option) => (
            <MenuItem
              key={option.value}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleMenuItemClick(option.value);
              }}
            >
              <CircleOption label={option.label} type="priority" />
            </MenuItem>
          ))}
      </Menu>
    </Box>
  );
};

export default CustomPrioritySelect;
